import { useNavigate, useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import { ATTRIBUTE_COLOURS } from '../../../../../../../../utils/buildFilterPills';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateDoc from '@/api/mutations/useUpdateDoc';
import { Doc } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Chip } from '@/new/components/chip';
import { DocRelationships } from '@/new/components/doc-viewer/doc-relationships';
import { EditableField } from '@/new/components/editable-field';
import { Icon } from '@/new/components/icon';
import { MetadataDisplay, getWexlerFieldsForDisplay, renderFieldValue } from '@/new/components/metadata-display';
import { ProcessingChip, getProcessingState } from '@/new/components/processing-chip';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { Label } from '@/new/components/shadcn/label';
import { cn } from '@/new/lib/utils';

interface DocDetailsPanelProps {
  doc: Doc & { keyFactsCount: number; relevantFactsCount: number };
}

export const DocDetailsPanel = ({ doc }: DocDetailsPanelProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const navigate = useNavigate();
  const { hasPermission } = useHasPermission();
  const canUpdateDoc = hasPermission(Permission.DocumentUpdate);

  const { mutate: updateDocFileName } = useUpdateDoc();
  const { mutate: updateDocAuthor } = useUpdateDoc();
  const { mutate: updateDocDate } = useUpdateDoc();

  const handleUpdateDocFileName = (value: string) => {
    updateDocFileName({ matterId, docId: doc.id, field: 'filename', value });
    trackCustomEvent(TrackingEventsV2.DocumentUpdateName, { documentId: doc.id });
  };

  const handleUpdateDocAuthor = (value: string) => {
    updateDocAuthor({ matterId, docId: doc.id, field: 'author', value });
    trackCustomEvent(TrackingEventsV2.DocumentUpdateAuthor, { documentId: doc.id });
  };

  const handleUpdateDocDate = (value: string) => {
    updateDocDate({ matterId, docId: doc.id, field: 'dateText', value });
    trackCustomEvent(TrackingEventsV2.DocumentUpdateDate, { documentId: doc.id });
  };

  const handleViewDocument = () => {
    navigate(`/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}`);
    trackCustomEvent(TrackingEventsV2.DocumentClickView, { documentId: doc.id });
  };

  const formatAuthorString = (author: string | undefined | null) => {
    if (author === undefined || author === null) {
      return '';
    }

    if (author.length < 50) {
      return author;
    }

    const authors = author.split(', ');
    let trimmedAuthorString = '';

    for (const authorItem of authors) {
      trimmedAuthorString += authorItem;

      if (trimmedAuthorString.length >= 50) {
        return `${trimmedAuthorString} ...`;
      }

      trimmedAuthorString += ', ';
    }

    return `${author.slice(0, 50)} ...`;
  };

  // Get Wexler fields for inline display
  const wexlerFields = doc.file.fileMetadata ? getWexlerFieldsForDisplay(doc.file.fileMetadata) : [];

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="rounded-tr-md border-b bg-gray-50 px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-semibold text-gray-800">
              {canUpdateDoc ? <EditableField value={doc.file.name} onSave={handleUpdateDocFileName} /> : doc.file.name}
            </h1>

            <div className="mt-2 flex items-center gap-2">
              <Chip className={cn('border', ATTRIBUTE_COLOURS.extension)}>{doc.file.extension.toUpperCase()}</Chip>

              {doc.type && <Chip className={cn('border', ATTRIBUTE_COLOURS.docType)}>{doc.type}</Chip>}

              {doc.keyFactsCount > 0 && (
                <Chip className={cn('border', ATTRIBUTE_COLOURS.hasKeyFacts)}>
                  <Icon className="lucide--key-round" />
                  {doc.keyFactsCount} Key Fact{doc.keyFactsCount !== 1 ? 's' : ''}
                </Chip>
              )}

              {doc.relevantFactsCount > 0 && (
                <Chip className={cn('border', ATTRIBUTE_COLOURS.hasRelevantFacts)}>
                  <Icon className="lucide--check" />
                  {doc.relevantFactsCount} Relevant Fact{doc.relevantFactsCount !== 1 ? 's' : ''}
                </Chip>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-3 px-6 py-6">
        {/* New AI-generated title */}
        {doc.title && <p className="text-base font-medium">{doc.title}</p>}

        {/* Summary */}
        {doc.summary && <div className="mb-6 rounded-md text-sm leading-relaxed text-gray-700">{doc.summary}</div>}

        {/* View Document Button */}
        <Button onClick={handleViewDocument} className="w-full justify-center py-2" variant="default">
          View Full Document
          <Icon className="lucide--arrow-right" />
        </Button>

        {/* Issues */}
        {doc.issues && doc.issues.length > 0 && (
          <div className="space-y-4">
            <Label className="text-xs uppercase">Issues</Label>
            <div className="my-2 flex flex-wrap gap-2">
              {doc.issues.map((issue, index) => (
                <Badge key={index} className="bg-blue-100/60 text-blue-600">
                  {issue}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Document Information */}
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Document Date</Label>
            <div className="text-sm text-gray-900">
              {canUpdateDoc ? (
                <EditableField value={doc.dateText || 'N/A'} onSave={handleUpdateDocDate} />
              ) : (
                doc.dateText || 'N/A'
              )}
            </div>
          </div>

          {doc.author && (
            <div className="space-y-1">
              <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Author</Label>
              <div className="text-sm text-gray-900">
                {canUpdateDoc ? (
                  <EditableField value={formatAuthorString(doc.author)} onSave={handleUpdateDocAuthor} />
                ) : (
                  formatAuthorString(doc.author)
                )}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Facts Count</Label>
            <div className="text-sm text-gray-900">{doc.factsCount || 0}</div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Upload Date</Label>
            <div className="text-sm text-gray-900">{formatDate(doc.createdDate)}</div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Status</Label>
            <div className="text-sm text-gray-900">
              <ProcessingChip processingState={getProcessingState(doc)} />
            </div>
          </div>

          {/* Wexler Extracted Metadata - displayed inline */}
          {wexlerFields.map((field, index) => (
            <div key={`wexler-${index}`} className="space-y-1">
              <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">{field.label}</Label>
              {renderFieldValue(field, { maxTextLength: 50 })}
            </div>
          ))}
        </div>

        {/* File Metadata */}
        {doc.file.fileMetadata && <MetadataDisplay metadata={doc.file.fileMetadata} />}

        {/* Document Relationships */}
        {((doc.file.parentFiles && doc.file.parentFiles.length > 0) ||
          (doc.file.childFiles && doc.file.childFiles.length > 0)) && <DocRelationships doc={doc} />}
      </div>
    </div>
  );
};
